import {
  DistinctChainKeyDict,
  DistinctDomainKeyDict,
  INITIAL_ANTIBODY_NDC_TRANSLATION_PARAMETERS,
  TranslationParameters,
} from 'antibodies/nested-data-creation/constants/AntibodyNestedDataCreationConstants';

// ------------------------------------------------------------
// Modal Types and Constants
// ------------------------------------------------------------

export enum AntibodySpreadsheetImportStepKey {
  SELECT_FORMAT = 'SELECT_FORMAT',
  UPLOAD_SPREADSHEET = 'UPLOAD_SPREADSHEET',
  COLUMN_MAPPING = 'COLUMN_MAPPING',
  FINALIZE = 'FINALIZE',
  /** Only shown if there are validation errors upon submission */
  VALIDATION_ERRORS = 'VALIDATION_ERRORS',
}

export const ANTIBODY_SPREADSHEET_IMPORT_STEPS = [
  {key: AntibodySpreadsheetImportStepKey.SELECT_FORMAT, title: 'Select format & inputs'},
  {key: AntibodySpreadsheetImportStepKey.UPLOAD_SPREADSHEET, title: 'Upload spreadsheet'},
  {key: AntibodySpreadsheetImportStepKey.COLUMN_MAPPING, title: 'Map columns'},
  {key: AntibodySpreadsheetImportStepKey.FINALIZE, title: 'Finalize & create'},
  {key: AntibodySpreadsheetImportStepKey.VALIDATION_ERRORS, title: 'Validation errors'},
];

export interface SelectFormatPane {
  paneKey: AntibodySpreadsheetImportStepKey.SELECT_FORMAT;
}

export interface UploadSpreadsheetPane {
  paneKey: AntibodySpreadsheetImportStepKey.UPLOAD_SPREADSHEET;
}

export interface ColumnMappingPane {
  paneKey: AntibodySpreadsheetImportStepKey.COLUMN_MAPPING;
}

export interface FinalizePane {
  paneKey: AntibodySpreadsheetImportStepKey.FINALIZE;
}

export interface ValidationErrorsPane {
  paneKey: AntibodySpreadsheetImportStepKey.VALIDATION_ERRORS;
}

export type Pane =
  | SelectFormatPane
  | UploadSpreadsheetPane
  | ColumnMappingPane
  | FinalizePane
  | ValidationErrorsPane;

export const NDC_EXISTING_ENTITY_COMPONENT_TYPE_LABEL = 'Entity name / Registry ID';
export const NDC_DNA_BASES_COMPONENT_TYPE_LABEL = 'DNA bases';
export const NDC_AA_SEQUENCE_COMPONENT_TYPE_LABEL = 'AA sequence';

// ------------------------------------------------------------
// NDC Spreadsheet Import Types
// ------------------------------------------------------------

// TODO(BNCH-142635): Rename to match backend once settled on naming.
export enum NdcStartingType {
  PAIRS = 'PAIRS',
  DOMAINS = 'DOMAINS',
  CHAINS = 'CHAINS',
}

export const NDC_STARTING_TYPE_LABELS: Record<NdcStartingType, string> = {
  [NdcStartingType.PAIRS]: 'Starting from Pairs',
  [NdcStartingType.DOMAINS]: 'Starting from Domains',
  [NdcStartingType.CHAINS]: 'Starting from Chains',
};

export enum NdcComponentSequenceType {
  DNA = 'DNA',
  AA = 'AA',
}

export enum NdcComponentInputType {
  NEW_SEQUENCE = 'NEW_SEQUENCE',
  EXISTING_ENTITY_NAME_OR_REGISTRY_ID = 'EXISTING_ENTITY_NAME_OR_REGISTRY_ID',
}

export interface TaggedDomainKeyDict extends DistinctDomainKeyDict {
  componentFormatType: 'DOMAIN';
}

export interface TaggedChainKeyDict extends DistinctChainKeyDict {
  componentFormatType: 'CHAIN';
}

export interface NdcComponentType {
  componentSequenceType: NdcComponentSequenceType;
  componentInputType: NdcComponentInputType;
}

/** Represents a single spreadsheet column
 *
 * TODO(BNCH-142635): Separate into union of types, consider adding a requiresConfirmation field instead of
 * implicit use of selectedType, which should instead just be set to what the user actually sees.
 */
export interface NdcColumnState {
  columnName: string;
  /** Null if there was an error inferring the column types */
  componentFormatKey: TaggedDomainKeyDict | TaggedChainKeyDict | null;
  /** Column inference returned error message, if any */
  errorMessage: string | null;
  /** The inferred types for the column, or null if there was an error inferring the column types */
  inferredTypes: Array<NdcComponentType> | null;
  /** The selected type for the column.
   *
   * If there is only one inferred type, selectedType is automatically set to that inferred type.
   * If there are multiple inferred types, selectedType is null until the user selects a type.
   * If the column has an error, selectedType is always null.
   *
   * The selectedType is used by the ColumnMappingPane to infer if it should display a warning to the user
   * that they should confirm the column type. It does not need to be set for the spreadsheet import to be
   * submitted, as in that case the 'highest priority' inferred type is used, i.e. the one that is displayed
   * to the user in the dropdown with the warning icon.
   */
  selectedType: NdcComponentType | null;
}

export interface NdcSpreadsheetInterpretation {
  /** The columns of the spreadsheet and their inferred/selected types, or error messages. The columns are in
   * the order they appear in the spreadsheet. */
  columns: Array<NdcColumnState>;
  /** Number of data rows, excluding header */
  rowCount: number;
}

export enum DnaDomainSchemaMode {
  SHARED_SCHEMA = 'SHARED_SCHEMA',
  SEPARATE_VARIABLE_AND_CONSTANT_SCHEMAS = 'SEPARATE_VARIABLE_AND_CONSTANT_SCHEMAS',
}

export type DnaComponentSchemaConfig =
  | {
      dnaDomainSchemaMode: DnaDomainSchemaMode.SHARED_SCHEMA;
      dnaDomainSchemaId: string | null;
      dnaChainSchemaId: string | null;
    }
  | {
      dnaDomainSchemaMode: DnaDomainSchemaMode.SEPARATE_VARIABLE_AND_CONSTANT_SCHEMAS;
      dnaDomainVariableSchemaId: string | null;
      dnaDomainConstantSchemaId: string | null;
      dnaChainSchemaId: string | null;
    };

export interface AntibodyNestedDataCreationSpreadsheetImportState {
  /** The format API ID of antibodies to be created */
  selectedFormatId: string | null;
  /** The schema API ID for the antibodies to be created */
  selectedSchemaId: string | null;
  /** The project folder API ID for the antibodies to be created */
  selectedProjectFolderId: string | null;
  /** The registry API ID */
  selectedRegistryId: string | null;
  /** The normalized configuration describing how DNA components will be translated into AA components */
  translationParameters: TranslationParameters;

  // Spreadsheet Upload
  /** The starting point for the spreadsheet import, determining which columns are required and what template
   * is available for the user to download */
  selectedStartingType: NdcStartingType;
  /** The uploaded spreadsheet file info */
  spreadsheetFile: File | null;
  /** The URL of the uploaded spreadsheet */
  spreadsheetURL: string | null;
  /** Backend-inferred column interpretations, and user edits to mappings */
  spreadsheetInterpretation: NdcSpreadsheetInterpretation | null;
  /** Column inference returned error message for spreadsheet-wide errors, e.g. missing columns */
  columnInferenceErrorMessage: string | null;

  // Finalize Pane
  /** Selected schemas for DNA components */
  dnaComponentSchemaConfig: DnaComponentSchemaConfig;
}

export const INITIAL_ANTIBODY_NDC_DNA_COMPONENT_SCHEMA_CONFIG: DnaComponentSchemaConfig = {
  dnaDomainSchemaMode: DnaDomainSchemaMode.SHARED_SCHEMA,
  dnaDomainSchemaId: null,
  dnaChainSchemaId: null,
};

export const INITIAL_ANTIBODY_NESTED_DATA_CREATION_SPREADSHEET_IMPORT_STATE: AntibodyNestedDataCreationSpreadsheetImportState =
  {
    selectedFormatId: null,
    selectedSchemaId: null,
    selectedProjectFolderId: null,
    selectedRegistryId: null,
    translationParameters: INITIAL_ANTIBODY_NDC_TRANSLATION_PARAMETERS,
    selectedStartingType: NdcStartingType.DOMAINS,
    spreadsheetFile: null,
    spreadsheetURL: null,
    spreadsheetInterpretation: null,
    columnInferenceErrorMessage: null,
    dnaComponentSchemaConfig: INITIAL_ANTIBODY_NDC_DNA_COMPONENT_SCHEMA_CONFIG,
  };

// ------------------------------------------------------------
// API Request and Response Types
// ------------------------------------------------------------

// Inferring Columns

export interface NdcColumnInferenceRequest {
  spreadsheetLocation: string;
  antibodySchemaId: string;
  componentFormatType: NdcStartingType;
}

export interface NdcColumnInferenceResult {
  responseType: 'INFERRED_COLUMN';
  columnName: string;
  componentFormatKey: TaggedDomainKeyDict | TaggedChainKeyDict;
  inferredComponentTypes: Array<NdcComponentType>;
}

export interface NdcColumnInferenceError {
  responseType: 'ERROR';
  columnName: string;
  errorMessage: string;
}

export function isNdcColumnInferenceResult(
  column: NdcColumnInferenceResult | NdcColumnInferenceError
): column is NdcColumnInferenceResult {
  // eslint-disable-next-line benchlint/no-in-operator
  return 'inferredComponentTypes' in column;
}

export function isNdcColumnInferenceError(
  column: NdcColumnInferenceResult | NdcColumnInferenceError
): column is NdcColumnInferenceError {
  // eslint-disable-next-line benchlint/no-in-operator
  return 'errorMessage' in column;
}
export interface NdcColumnInferenceResponse {
  columnTypes: Array<NdcColumnInferenceResult | NdcColumnInferenceError>;
  rowCount: number;
}

export function isNdcColumnInferenceResponse(
  response: NdcColumnInferenceResponse | {errorMessage: string}
): response is NdcColumnInferenceResponse {
  // eslint-disable-next-line benchlint/no-in-operator
  return 'columnTypes' in response;
}

// Importing Spreadsheet

/* Flattened version of NdcColumnType that unifies selected and inferred column types */
export type NdcSpreadsheetImportColumnType = Omit<
  NdcColumnState,
  'inferredTypes' | 'selectedType' | 'errorMessage'
> &
  NdcComponentType;

export interface NdcSpreadsheetImportRequest {
  projectFolderId: string;
  antibodySchemaId: string;
  spreadsheetLocation: string;
  // TODO(BNCH-142635): Check if filename is required
  componentType: NdcStartingType;
  columnTypes: Array<NdcSpreadsheetImportColumnType>;
  translationParameters: TranslationParameters;
}

/** Response from the backend when there are no validation errors */
// TODO(BNCH-142635): Update this to match the backend success response shape
export interface NdcSpreadsheetImportResponse {
  ndcWorkspaceUrl?: string;
}

export interface NdcSpreadsheetImportValidationError {
  rowIndex: number;
  errorMessage: string;
}

/** When there are validation errors, the backend will throw a BadRequestError of this shape */
export interface NdcSpreadsheetImportValidationErrorBadRequest {
  validationErrors: Array<NdcSpreadsheetImportValidationError>;
}
